import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface LiveSessionRow {
  id: string;
  scheduled_date: string | null;
  type: string | null;
  title: string;
  cohort_id: string | null;
  cohort_name: string | null;
  organization_id: string | null;
  organization_name: string | null;
  expert_id: string | null;
  expert_name: string | null;
  expert_avatar: string | null;
  start_hour: number | null;
  start_minute: number | null;
  end_hour: number | null;
  end_minute: number | null;
  duration_hours: number;
  recording_link: string | null;
  has_recording: boolean;
  connection_link: string | null;
  ref_id: string | null;
  requires_deliverable: boolean;
  deliverables_expected: number;
  deliverables_received: number;
  payout_id: string | null;
  payout_status: string | null;
}

export interface LiveSessionsFilters {
  monthStart: string; // YYYY-MM-01
  monthEnd: string;   // YYYY-MM-01 next month
  organizationId?: string | null;
  cohortId?: string | null;
  expertId?: string | null;
  type?: 'workshop' | 'mentoring' | 'mentorship' | 'all';
  onlyNoRecording?: boolean;
}

function calcHours(item: any): number {
  const sh = item.start_hour ?? 0;
  const sm = item.start_minute ?? 0;
  const eh = item.end_hour ?? 0;
  const em = item.end_minute ?? 0;
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  return Math.max(0, mins) / 60;
}

export function useLiveSessionsAudit(filters: LiveSessionsFilters) {
  return useQuery({
    queryKey: ['live-sessions-audit', filters],
    queryFn: async (): Promise<LiveSessionRow[]> => {
      const today = new Date().toISOString().slice(0, 10);

      const sb: any = supabase;
      let query = sb
        .from('cohort_schedule_items')
        .select(`
          id, cohort_id, type, title, scheduled_date,
          start_hour, start_minute, end_hour, end_minute,
          recording_link, meet_recording_link, connection_link, ref_id, expert_id,
          profiles:profiles!cohort_schedule_items_expert_id_fkey(id, full_name, avatar_url),
          cohorts!inner(id, name, organization_id, organizations(id, name))
        `)
        .in('type', ['workshop', 'mentoring', 'mentorship'])
        .gte('scheduled_date', filters.monthStart)
        .lt('scheduled_date', filters.monthEnd)
        .lte('scheduled_date', today)
        .order('scheduled_date', { ascending: false });

      if (filters.type && filters.type !== 'all') {
        if (filters.type === 'mentoring' || filters.type === 'mentorship') {
          query = query.in('type', ['mentoring', 'mentorship']);
        } else {
          query = query.eq('type', filters.type);
        }
      }
      if (filters.cohortId) query = query.eq('cohort_id', filters.cohortId);
      if (filters.expertId) query = query.eq('expert_id', filters.expertId);

      const { data, error } = await query;
      if (error) throw error;
      let rows = (data ?? []) as any[];

      if (filters.organizationId) {
        rows = rows.filter((r) => r.cohorts?.organization_id === filters.organizationId);
      }
      if (filters.onlyNoRecording) {
        rows = rows.filter((r) => !r.meet_recording_link || !r.meet_recording_link.trim());
      }

      // Workshop deliverables info
      const workshopIds = rows.filter((r) => r.type === 'workshop' && r.ref_id).map((r) => r.ref_id!);
      let workshopReqMap: Record<string, boolean> = {};
      if (workshopIds.length) {
        const { data: ws } = await supabase
          .from('program_workshops')
          .select('id, requires_deliverable')
          .in('id', workshopIds);
        (ws ?? []).forEach((w: any) => { workshopReqMap[w.id] = !!w.requires_deliverable; });
      }

      // Expected participants per cohort + set of active user_ids per cohort
      const cohortIds = Array.from(new Set(rows.map((r) => r.cohort_id).filter(Boolean)));
      const cohortPartCount: Record<string, number> = {};
      const cohortPartUsers: Record<string, Set<string>> = {};
      if (cohortIds.length) {
        const { data: cps } = await supabase
          .from('cohort_participants')
          .select('cohort_id, user_id')
          .in('cohort_id', cohortIds);
        (cps ?? []).forEach((cp: any) => {
          cohortPartCount[cp.cohort_id] = (cohortPartCount[cp.cohort_id] ?? 0) + 1;
          if (!cohortPartUsers[cp.cohort_id]) cohortPartUsers[cp.cohort_id] = new Set();
          cohortPartUsers[cp.cohort_id].add(cp.user_id);
        });
      }

      // Map workshop_id (ref_id) -> cohort_id to validate deliverable belongs to an active participant
      const workshopToCohort: Record<string, string> = {};
      rows.forEach((r) => {
        if (r.type === 'workshop' && r.ref_id && r.cohort_id) {
          workshopToCohort[r.ref_id] = r.cohort_id;
        }
      });

      // Received deliverables per workshop: only submitted/validated/needs_revision
      // AND only counting users that currently belong to the workshop's cohort.
      const deliveredCount: Record<string, number> = {};
      if (workshopIds.length) {
        const { data: ds } = await sb
          .from('participant_deliverables')
          .select('workshop_id, user_id, status')
          .in('workshop_id', workshopIds)
          .in('status', ['submitted', 'validated']);
        (ds ?? []).forEach((d: any) => {
          const cohortId = workshopToCohort[d.workshop_id];
          if (!cohortId) return;
          if (!cohortPartUsers[cohortId]?.has(d.user_id)) return;
          deliveredCount[d.workshop_id] = (deliveredCount[d.workshop_id] ?? 0) + 1;
        });
      }

      // Linked payouts
      const scheduleIds = rows.map((r) => r.id);
      const scheduleToPayout: Record<string, { id: string; status: string }> = {};
      if (scheduleIds.length) {
        const { data: items } = await sb
          .from('expert_payout_items')
          .select('schedule_item_id, payout_id, expert_payouts!inner(id, status)')
          .in('schedule_item_id', scheduleIds);
        (items ?? []).forEach((it: any) => {
          if (it.expert_payouts && it.expert_payouts.status !== 'cancelled') {
            scheduleToPayout[it.schedule_item_id] = { id: it.payout_id, status: it.expert_payouts.status };
          }
        });
      }

      return rows.map((r): LiveSessionRow => {
        // Solo Meet nativo cuenta como grabación visible (Fireflies oculto en UI)
        const rec = (r.meet_recording_link ?? '').trim();
        const expWs = r.type === 'workshop' && r.ref_id && workshopReqMap[r.ref_id];
        return {
          id: r.id,
          scheduled_date: r.scheduled_date,
          type: r.type,
          title: r.title || 'Sesión',
          cohort_id: r.cohort_id,
          cohort_name: r.cohorts?.name ?? null,
          organization_id: r.cohorts?.organization_id ?? null,
          organization_name: r.cohorts?.organizations?.name ?? null,
          expert_id: r.expert_id,
          expert_name: r.profiles?.full_name ?? null,
          expert_avatar: r.profiles?.avatar_url ?? null,
          start_hour: r.start_hour, start_minute: r.start_minute,
          end_hour: r.end_hour, end_minute: r.end_minute,
          duration_hours: calcHours(r),
          recording_link: r.meet_recording_link,
          has_recording: rec.length > 0,
          connection_link: r.connection_link,
          ref_id: r.ref_id,
          requires_deliverable: !!expWs,
          deliverables_expected: expWs ? (cohortPartCount[r.cohort_id!] ?? 0) : 0,
          deliverables_received: r.ref_id ? (deliveredCount[r.ref_id] ?? 0) : 0,
          payout_id: scheduleToPayout[r.id]?.id ?? null,
          payout_status: scheduleToPayout[r.id]?.status ?? null,
        };
      });
    },
  });
}

export function useExpertsList() {
  return useQuery({
    queryKey: ['experts-with-sessions'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('cohort_schedule_items')
        .select('expert_id, profiles!cohort_schedule_items_expert_id_fkey(id, full_name, avatar_url)')
        .not('expert_id', 'is', null)
        .in('type', ['workshop', 'mentoring', 'mentorship']);
      if (error) throw error;
      const map = new Map<string, { id: string; full_name: string; avatar_url: string | null }>();
      (data ?? []).forEach((r: any) => {
        if (r.expert_id && r.profiles) map.set(r.expert_id, r.profiles);
      });
      return Array.from(map.values()).sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''));
    },
  });
}
